import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { EyeOff, Shield, Users, Lock, CheckCircle, AlertTriangle } from 'lucide-react';

// Privacy Reminder Component with popup explanation
export default function PrivacyReminder({ className = '' }) {
  const [showDialog, setShowDialog] = useState(false);

  return (
    <>
      {/* Inline Reminder */}
      <div className={`flex items-center gap-2 text-sm text-slate-500 ${className}`}>
        <EyeOff className="h-4 w-4 text-[#5AA2AE]" />
        <span>Your responses are anonymous.</span>
        <button
          onClick={() => setShowDialog(true)}
          className="text-[#5AA2AE] hover:text-[#5AA2AE]/80 hover:underline font-medium"
        >
          Learn how we protect your privacy
        </button>
      </div>

      {/* Privacy Explanation Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              <div className="p-2 rounded-full bg-[#5AA2AE]/10">
                <EyeOff className="h-6 w-6 text-[#5AA2AE]" />
              </div>
              Your Privacy is Protected
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Introduction */}
            <p className="text-slate-600">
              We understand that honest feedback requires trust. That's why Pulse is designed 
              from the ground up to protect your identity while still providing valuable insights.
            </p>

            {/* How it Works Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Shield className="h-5 w-5 text-[#5AA2AE]" />
                How Your Responses Stay Anonymous
              </h3>
              
              {/* Visual: Segment-based Responses */}
              <div className="bg-slate-50 rounded-xl p-5">
                <p className="text-sm text-slate-600 mb-4">
                  Your individual responses are never shown. Results are grouped by segment, 
                  and only displayed when enough people respond:
                </p>
                
                {/* Segments with users */}
                <div className="grid grid-cols-2 gap-6 mb-6">
                  {/* Segment A - 3 responses */}
                  <div className="bg-white rounded-lg p-4 border">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-emerald-500" />
                      <span className="font-medium text-sm">Segment A</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="h-2 flex-1 bg-emerald-200 rounded" />
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="h-2 flex-1 bg-emerald-200 rounded" />
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="h-2 flex-1 bg-emerald-200 rounded" />
                      </div>
                    </div>
                    <p className="text-xs text-emerald-600 mt-2 font-medium">3 of 3 responded</p>
                  </div>
                  
                  {/* Segment B - 1 of 2 responses */}
                  <div className="bg-white rounded-lg p-4 border">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-3 h-3 rounded-full bg-amber-500" />
                      <span className="font-medium text-sm">Segment B</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-amber-600" />
                        </div>
                        <div className="h-2 flex-1 bg-amber-200 rounded" />
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center border-2 border-dashed border-slate-300">
                          <span className="text-slate-400 text-xs">?</span>
                        </div>
                        <div className="h-2 flex-1 bg-slate-200 rounded border border-dashed border-slate-300" />
                      </div>
                    </div>
                    <p className="text-xs text-amber-600 mt-2 font-medium">1 of 2 responded</p>
                  </div>
                </div>
                
                {/* Arrow */}
                <div className="flex justify-center mb-4">
                  <div className="flex flex-col items-center">
                    <div className="text-2xl text-slate-300">↓</div>
                    <span className="text-xs text-slate-400">Results by Segment</span>
                  </div>
                </div>
                
                {/* Results showing visibility */}
                <div className="grid grid-cols-2 gap-6">
                  {/* Segment A Result - Visible */}
                  <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-emerald-500" />
                        <span className="font-medium text-sm text-emerald-800">Segment A</span>
                      </div>
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    </div>
                    {/* Progress bar at 72% */}
                    <div className="h-3 bg-emerald-100 rounded mb-2 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded" style={{ width: '72%' }} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-emerald-700">Average: 7.2 / 10</span>
                      <span className="text-xs font-medium text-emerald-600">Results Visible</span>
                    </div>
                  </div>
                  
                  {/* Segment B Result - Blocked */}
                  <div className="bg-slate-100 rounded-lg p-4 border border-slate-300">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-slate-400" />
                        <span className="font-medium text-sm text-slate-500">Segment B</span>
                      </div>
                      <EyeOff className="w-5 h-5 text-slate-400" />
                    </div>
                    {/* Grey hashed bar */}
                    <div className="h-3 bg-slate-200 rounded mb-2 overflow-hidden relative">
                      <div 
                        className="absolute inset-0" 
                        style={{ 
                          background: 'repeating-linear-gradient(45deg, #94a3b8, #94a3b8 2px, #cbd5e1 2px, #cbd5e1 6px)' 
                        }} 
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Need 2 more responses</span>
                      <span className="text-xs font-medium text-slate-500">Results Hidden</span>
                    </div>
                  </div>
                </div>
                
                {/* Text Analysis Example */}
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <p className="text-xs text-slate-500 mb-3 font-medium uppercase tracking-wide">Text Analysis Example</p>
                  <div className="grid grid-cols-2 gap-6">
                    {/* Segment A - Text visible */}
                    <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full bg-emerald-500" />
                        <span className="text-xs font-medium text-emerald-800">Segment A</span>
                      </div>
                      <p className="text-sm text-emerald-900 italic">
                        "Segment A is concerned about project timelines, a sharp increase from last month."
                      </p>
                    </div>
                    
                    {/* Segment B - Text hidden */}
                    <div className="bg-slate-100 rounded-lg p-3 border border-slate-300">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full bg-slate-400" />
                        <span className="text-xs font-medium text-slate-500">Segment B</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400">
                        <EyeOff className="w-4 h-4" />
                        <span className="text-sm italic">Insights hidden until threshold met</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Anonymity Threshold Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Lock className="h-5 w-5 text-[#5AA2AE]" />
                The Anonymity Threshold
              </h3>
              
              <p className="text-sm text-slate-600">
                To ensure your identity can't be guessed, we require a minimum number of 
                responses before showing any results:
              </p>
              
              {/* Visual: Threshold Comparison */}
              <div className="grid grid-cols-2 gap-4">
                {/* Below Threshold */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                    <span className="font-medium text-amber-800">Below Threshold</span>
                  </div>
                  
                  <div className="space-y-2 mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-amber-200" />
                      <div className="h-2 flex-1 bg-amber-200 rounded" />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-amber-200" />
                      <div className="h-2 flex-1 bg-amber-200 rounded" />
                    </div>
                    <div className="flex items-center gap-2 opacity-30">
                      <div className="w-6 h-6 rounded-full bg-slate-200 border-2 border-dashed border-slate-300" />
                      <div className="h-2 flex-1 bg-slate-200 rounded border-2 border-dashed border-slate-300" />
                    </div>
                  </div>
                  
                  <div className="bg-amber-100 rounded-lg p-3 text-center">
                    <div className="flex items-center justify-center gap-2 text-amber-800">
                      <EyeOff className="h-4 w-4" />
                      <span className="text-sm font-medium">Results Hidden</span>
                    </div>
                    <p className="text-xs text-amber-600 mt-1">Only 2 of 3 minimum responses</p>
                  </div>
                </div>
                
                {/* Above Threshold */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="h-5 w-5 text-emerald-600" />
                    <span className="font-medium text-emerald-800">Above Threshold</span>
                  </div>
                  
                  <div className="space-y-2 mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-emerald-200" />
                      <div className="h-2 flex-1 bg-emerald-300 rounded" />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-emerald-200" />
                      <div className="h-2 flex-1 bg-emerald-300 rounded" />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-emerald-200" />
                      <div className="h-2 flex-1 bg-emerald-300 rounded" />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-emerald-200" />
                      <div className="h-2 flex-1 bg-emerald-300 rounded" />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-emerald-200" />
                      <div className="h-2 flex-1 bg-emerald-300 rounded" />
                    </div>
                  </div>
                  
                  <div className="bg-emerald-100 rounded-lg p-3 text-center">
                    <div className="flex items-center justify-center gap-2 text-emerald-800">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">Results Visible</span>
                    </div>
                    <p className="text-xs text-emerald-600 mt-1">5 responses meet the threshold</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Points */}
            <div className="bg-[#5AA2AE]/5 rounded-xl p-5 space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Shield className="h-5 w-5 text-[#5AA2AE]" />
                What This Means For You
              </h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-[#5AA2AE] mt-0.5 shrink-0" />
                  <span><strong>No one sees your individual response</strong> - only grouped averages and summaries</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-[#5AA2AE] mt-0.5 shrink-0" />
                  <span><strong>Small groups are protected</strong> - results are hidden until enough people respond</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-[#5AA2AE] mt-0.5 shrink-0" />
                  <span><strong>Text responses are summarized</strong> - AI creates themes without attributing quotes</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-[#5AA2AE] mt-0.5 shrink-0" />
                  <span><strong>Admins can't identify you</strong> - even project administrators only see aggregated data</span>
                </li>
              </ul>
            </div>

            {/* Closing message */}
            <p className="text-sm text-slate-500 text-center italic">
              Your honest feedback helps improve the project for everyone. 
              Thank you for sharing your perspective.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
